import {
  Text,
  Box,
  Flex,
  TableContainer,
  Table,
  useToken,
  InputGroup,
  InputLeftElement,
  Icon,
  Input,
  Button,
  FormControl,
  FormErrorMessage,
  Select,
} from "@chakra-ui/react";
import React, { Fragment, useState } from "react";
import OutlinedButton from "../../components/themeComponents/OutlinedButton";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { fetchSegmentList } from "../../Services/api";
import { connect } from "react-redux";
import TableHead from "../Campaigns/components/TableHead";
import SegmentTable from "./SegmentTable";
import { OvalSpinner } from "../../components/Loaders";
import hexToRgba from "hex-to-rgba";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import { BiSearch } from "react-icons/bi";
import Pagination from "../../components/Pagination"; // Import the generic Pagination component

function SegmentList({ orgId, orgToken }) {
  if (orgToken) {
    orgId = orgToken;
  }

  const navigate = useNavigate();
  const [totalCount, setTotalCount] = useState(0);
  const [mainColor] = useToken("colors", ["primary.main"]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchError, setSearchError] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const customSegmentListQuery = useQuery(
    ["customSegmentListQuery", orgId, currentPage, searchTerm, statusFilter],
    async () => {
      setIsSearching(true);
      const resp = await fetchSegmentList(
        orgId,
        currentPage,
        searchTerm,
        statusFilter
      );
      setTotalCount(resp?.data?.data[0]?._total_count ?? 0);
      setIsSearching(false);
      return resp?.data?.data ?? [];
    },
    {
      staleTime: 0, // Ensure fresh data on page change
      cacheTime: 0,
      retry: 1,
      enabled: !!orgId, // Only fetch if orgId exists
    }
  );

  const handleSearch = () => {
    if (searchInput.length === 0 || searchInput.length >= 3) {
      setSearchError("");
      setCurrentPage(1);
      setSearchTerm(searchInput);
    } else {
      setSearchError("Please enter at least 3 characters to search.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Fragment>
      <Box w="100%" h="100%" overflowY="scroll">
        {/* Header section with title and create segment button */}
        <Flex justifyContent="space-between" alignItems="center">
          <Text as="b" fontSize="2xl">
            Segment List
          </Text>
          {!window.location.href.includes("all-banks") && (
            <OutlinedButton
              width="10vw"
              borderRadius="8px"
              label="Create Segment"
              onClick={() => navigate("/audience/segment/create")}
            />
          )}
        </Flex>

        {/* Search and filter controls */}
        <Flex alignItems="flex-start" gap={4} mt={4} wrap="wrap">
          <FormControl
            width="300px"
            isInvalid={!!searchError}
            display="flex"
            flexDirection="column"
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={BiSearch} color="gray.500" />
              </InputLeftElement>
              <Input
                placeholder="Search segments..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
            <FormErrorMessage>{searchError}</FormErrorMessage>
          </FormControl>

          <Select
            width="200px"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1); // Reset to first page on filter change
            }}
          >
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
            <option value="all">All</option>
          </Select>

          <Button onClick={handleSearch} isLoading={isSearching}>
            Search
          </Button>
        </Flex>

        {/* Segment Table Container */}
        <TableContainer mt={3}>
          {customSegmentListQuery.isLoading && (
            <Flex justifyContent="center" alignItems="center" h="200px">
              <OvalSpinner width={50} height={50} />
            </Flex>
          )}
          {!customSegmentListQuery.isLoading &&
            !customSegmentListQuery?.data?.length && (
              <Flex
                direction="column"
                justifyContent="center"
                alignItems="center"
                py={10}
              >
                <Text fontSize="lg" fontWeight="bold" color="gray.600">
                  No segments created.
                </Text>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Create a segment to use in your workflows and improve
                  engagement.
                </Text>
                <PrimaryButton
                  label="Create Segment Now"
                  mt={4}
                  onClick={() => navigate("/audience/segment/create")}
                  size="md"
                />
              </Flex>
            )}
          {!customSegmentListQuery.isLoading &&
            customSegmentListQuery?.data?.length > 0 && (
              <Box>
                <Table>
                  <TableHead
                    headersProps={[
                      "Segment Name",
                      "Valid Phone",
                      "Valid Email",
                      "Total Customers",
                      "Actions",
                    ]}
                  />
                  <SegmentTable
                    list={customSegmentListQuery.data}
                    customSegmentListQuery={customSegmentListQuery}
                    orgId={orgId}
                  />
                </Table>
              </Box>
            )}
          {!customSegmentListQuery.isLoading &&
            customSegmentListQuery?.data?.length > 0 && (
              <Pagination
                totalCount={totalCount}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                isDisabled={
                  customSegmentListQuery.isLoading || isSearching
                }
              />
            )}
        </TableContainer>
      </Box>
    </Fragment>
  );
}

const mapStatetoProps = (state) => {
  return {
    ...state,
    orgId: state.auth.selectedOrg.token,
  };
};

export default connect(mapStatetoProps)(SegmentList);